import { CartContext, CartStatus } from '@petplate/ui/hooks/useShopifyCart';
import { LocalStorageService, SHOPIFY_CART_ID_KEY } from '@petplate/ui/lib/storage';
import { checkRequestFailed, storefrontClient } from '@petplate/utils/shopify';
import { StorefrontQueries } from '@petplate/utils/shopify/queries';
import type {
  CartBuyerIdentityInput,
  CartCreateInput,
  CartLineInput,
  CartLineUpdateInput,
  ShopifyAttribute,
  ShopifyCart,
  ShopifyCartCreateQuery,
  ShopifyCartQuery
} from '@petplate/utils/shopify/types';
import { captureException } from '@sentry/react';
import map from 'lodash/map';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CartActionEnum,
  CartActionQueries,
  type CartAction,
  type ShopifyCartProviderProps
} from './types';

export const ShopifyCartProvider = ({ children, ...callbacks }: ShopifyCartProviderProps) => {
  // Used to prevent multiple cart creation/fetching during hooks
  const startingCart = useRef(false);
  const cartReady = useRef(false);
  const [cart, setCart] = useState<ShopifyCart | null>(null);
  const [cartStatus, setCartStatus] = useState<CartStatus>(CartStatus.Uninitialized);

  /**
   * Creates an empty cart and overwrites existing data
   * @returns The id of the created cart
   */
  const createCart = useCallback(
    async (createInput?: CartCreateInput) => {
      try {
        setCartStatus(CartStatus.Creating);
        callbacks.onCreate?.();
        const { lines, discountCodes, buyerIdentity } = createInput ?? {};
        const resp = await storefrontClient.request<ShopifyCartCreateQuery>(
          StorefrontQueries.CartCreate,
          { variables: { lines, discountCodes, buyerIdentity } }
        );
        if (checkRequestFailed(resp) || resp.data?.cartCreate.userErrors.length) {
          const errorDetails =
            resp.errors?.message || JSON.stringify(resp.data?.cartCreate.userErrors);
          throw new Error(`Error creating cart. Errors: ${errorDetails}`);
        }
        if (resp.data?.cartCreate.cart) {
          setCart(resp.data.cartCreate.cart);
          setCartStatus(CartStatus.Idle);
          LocalStorageService.set(SHOPIFY_CART_ID_KEY, resp.data.cartCreate.cart.id);
          callbacks.onCreateComplete?.();
          cartReady.current = true;
          return resp.data.cartCreate.cart.id;
        }
        throw new Error('Failed to create cart. Unknown error.');
      } catch (e) {
        captureException(e);
        setCartStatus(CartStatus.Uninitialized);
        cartReady.current = false;
      } finally {
        startingCart.current = false;
      }
    },
    [callbacks.onCreate, callbacks.onCreateComplete]
  );

  /**
   * Fetches an existing cart or creates a new one if it fails to fetch
   * @param cartId - The id of the cart to fetch
   * @returns The id of the fetched/created cart
   */
  const fetchCart = useCallback(
    async (cartId: string) => {
      try {
        setCartStatus(CartStatus.Fetching);
        const resp = await storefrontClient.request<ShopifyCartQuery>(StorefrontQueries.Cart, {
          variables: { id: cartId }
        });
        if (checkRequestFailed(resp)) {
          throw new Error(`Error fetching cart with id=${cartId}. Errors: ${resp.errors?.message}`);
        }
        if (!resp.data?.cart) {
          // cart will also be null if it was already "completed"
          throw new Error(`Cart with id=${cartId} not found. Creating a new cart...`);
        }
        setCart(resp.data.cart);
        setCartStatus(CartStatus.Idle);
        cartReady.current = true;
      } catch (e) {
        setCartStatus(CartStatus.Uninitialized);
        LocalStorageService.delete(SHOPIFY_CART_ID_KEY);
        return await createCart();
      } finally {
        startingCart.current = false;
      }
    },
    [createCart]
  );

  const handleBeforeAction = useCallback(
    (action: CartAction) => {
      try {
        switch (action.type) {
          case CartActionEnum.AddLines:
            return callbacks.onLinesAdd?.();
          case CartActionEnum.UpdateLines:
            return callbacks.onLinesUpdate?.();
          case CartActionEnum.RemoveLines:
            return callbacks.onLinesRemove?.();
          case CartActionEnum.UpdateNote:
            return callbacks.onNoteUpdate?.();
          case CartActionEnum.UpdateBuyerIdentity:
            return callbacks.onBuyerIdentityUpdate?.();
          case CartActionEnum.UpdateAttributes:
            return callbacks.onAttributesUpdate?.();
          case CartActionEnum.UpdateDiscountCodes:
            return callbacks.onDiscountCodesUpdate?.();
        }
      } catch (e) {
        captureException(e);
        console.warn('Cart before action callback failed', e);
      }
    },
    [
      callbacks.onCreate,
      callbacks.onLinesAdd,
      callbacks.onLinesUpdate,
      callbacks.onLinesRemove,
      callbacks.onNoteUpdate,
      callbacks.onBuyerIdentityUpdate,
      callbacks.onAttributesUpdate,
      callbacks.onDiscountCodesUpdate
    ]
  );

  const handleSuccessAction = useCallback(
    (action: CartAction) => {
      try {
        switch (action.type) {
          case CartActionEnum.AddLines:
            return callbacks.onLinesAddComplete?.(action.payload.lines);
          case CartActionEnum.UpdateLines:
            return callbacks.onLinesUpdateComplete?.(action.payload.lines);
          case CartActionEnum.RemoveLines:
            return callbacks.onLinesRemoveComplete?.();
          case CartActionEnum.UpdateNote:
            return callbacks.onNoteUpdateComplete?.();
          case CartActionEnum.UpdateBuyerIdentity:
            return callbacks.onBuyerIdentityUpdateComplete?.();
          case CartActionEnum.UpdateAttributes:
            return callbacks.onAttributesUpdateComplete?.();
          case CartActionEnum.UpdateDiscountCodes:
            return callbacks.onDiscountCodesUpdateComplete?.();
        }
      } catch (e) {
        captureException(e);
        console.warn('Cart action success callback failed', e);
      }
    },
    [
      callbacks.onLinesAddComplete,
      callbacks.onLinesUpdateComplete,
      callbacks.onLinesRemoveComplete,
      callbacks.onNoteUpdateComplete,
      callbacks.onBuyerIdentityUpdateComplete,
      callbacks.onAttributesUpdateComplete,
      callbacks.onDiscountCodesUpdateComplete
    ]
  );

  /**
   * Handler for all cart update actions. Performs the action and updates the cart state
   * @param action - The action to perform
   */
  const handleCartAction = useCallback(
    async (action: CartAction) => {
      if (!cart?.id) throw new Error('There is no active cart');
      try {
        setCartStatus(CartStatus.Updating);
        handleBeforeAction(action);
        const resp = await storefrontClient.request(CartActionQueries[action.type], {
          variables: { cartId: cart.id, ...action.payload }
        });
        if (checkRequestFailed(resp) || resp.data[action.type].userErrors.length) {
          const errorDetails =
            resp.errors?.message || JSON.stringify(resp.data[action.type].userErrors);
          throw new Error(`Error updating cart. action="${action}"; errors="${errorDetails}"`);
        }
        const newCartData: ShopifyCart = resp.data[action.type].cart;
        if (!newCartData) {
          throw new Error(`Error updating cart. action="${action}" errors="Cart not found."`);
        }
        // Try to keep the most recent data when multiple requests are fired at the same time
        // NOTE: Avoid calling multiple actions at the same time, because the cart state might become inconsistent
        if (DateTime.fromISO(newCartData.updatedAt) >= DateTime.fromISO(cart.updatedAt)) {
          setCart(newCartData);
        }
        handleSuccessAction(action);
      } finally {
        setCartStatus(CartStatus.Idle);
      }
    },
    [cart?.id, handleBeforeAction, handleSuccessAction]
  );

  // Fetch existing cart or start a new one
  useEffect(() => {
    const storedCartId: string = LocalStorageService.get(SHOPIFY_CART_ID_KEY);
    if (!cart?.id && cartStatus === CartStatus.Uninitialized && !startingCart.current) {
      startingCart.current = true;
      if (storedCartId) fetchCart(storedCartId);
      else createCart();
    }
  }, [cart?.id, cartStatus, startingCart.current]);

  const cartContextValue = useMemo(
    () => ({
      // Cart state
      cartId: cart?.id,
      cartReady: cartReady.current,
      lines: cart?.lines.nodes || [],
      buyerIdentity: cart?.buyerIdentity,
      attributes: cart?.attributes || [],
      discountCodes: cart?.discountCodes || [],
      cost: cart?.cost,
      checkoutUrl: cart?.checkoutUrl,
      note: cart?.note,
      status: cartStatus,
      // Cart Actions
      cartCreate: createCart,
      linesAdd: async (lines: CartLineInput[]) => {
        if (!cart?.id) {
          await createCart({ lines });
        } else {
          await handleCartAction({ type: CartActionEnum.AddLines, payload: { lines } });
        }
      },
      linesUpdate: async (lines: CartLineUpdateInput[]) => {
        await handleCartAction({ type: CartActionEnum.UpdateLines, payload: { lines } });
      },
      linesRemove: async (lineIds: string[]) => {
        await handleCartAction({ type: CartActionEnum.RemoveLines, payload: { lineIds } });
      },
      cartClear: async () => {
        await handleCartAction({
          type: CartActionEnum.RemoveLines,
          payload: { lineIds: map(cart?.lines.nodes, 'id') }
        });
      },
      noteUpdate: async (note: string) => {
        await handleCartAction({ type: CartActionEnum.UpdateNote, payload: { note } });
      },
      buyerIdentityUpdate: async (buyerIdentity: CartBuyerIdentityInput) => {
        await handleCartAction({
          type: CartActionEnum.UpdateBuyerIdentity,
          payload: { buyerIdentity }
        });
      },
      attributesUpdate: async (attributes: ShopifyAttribute[]) => {
        await handleCartAction({
          type: CartActionEnum.UpdateAttributes,
          payload: { attributes }
        });
      },
      discountCodesUpdate: async (discountCodes: string[]) => {
        await handleCartAction({
          type: CartActionEnum.UpdateDiscountCodes,
          payload: { discountCodes }
        });
      }
    }),
    [cart, cartStatus, createCart, handleCartAction]
  );

  return <CartContext.Provider value={cartContextValue}>{children}</CartContext.Provider>;
};
